import { Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { FeatureHighlight } from '@/components/landing/FeatureHighlight'
import { useSEO } from '@/hooks/useSEO'

const services = [
  {
    id: 'blis',
    name: 'BLIS',
    title: 'Locations Analytics',
    description:
      'Analyze cities and locations to find the best grocery store opportunities, with insights into nearby competitors and key points of interest.',
    icon: 'analytics',
    iconColor: 'bg-blue-50 p-3 text-primary dark:bg-blue-900/20 dark:text-blue-300',
    badge: 'Analytics',
    features: [
      'AI-Powered Location Scoring',
      'Competitor & POI Insights',
      'Interactive Maps & Reports',
    ],
    gradientColor: 'bg-blue-100 dark:bg-blue-900/30',
    image: '/images/BLISmap.png',
  },
  {
    id: 'izg',
    name: 'IZG',
    title: 'Real-time Zones',
    description:
      'Intelligent Zone Geofencing for immediate boundary control. Get alerts the second an asset moves.',
    icon: 'radar',
    iconColor: 'bg-emerald-50 p-3 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    badge: 'Monitoring',
    features: [
      'Smart AI Zoning for Every City',
      'Faster Routes. More Visits. Higher Productivity',
      'One Platform to Control Customers, Zones & Agents',
    ],
    gradientColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    image: '/images/IZGmap.png',
  },
]

const features = [
  {
    icon: 'security',
    title: 'Secure Organizations',
    description:
      'Multi-tenant architecture ensuring isolated data environments with enterprise-grade encryption for total peace of mind.',
  },
  {
    icon: 'payments',
    title: 'Flexible Subscriptions',
    description:
      'Pay-as-you-go or committed use contracts. Our adaptable billing engine grows seamlessly with your business needs.',
  },
  {
    icon: 'monitoring',
    title: 'Real-time Usage',
    description:
      'Track API calls, active devices, and storage metrics in real-time through our transparent usage dashboard.',
  },
]


export const LandingPage = () => {
  const servicesSectionRef = useRef<HTMLElement>(null)
  const [isServicesVisible, setIsServicesVisible] = useState(false)
  const [hoveredServiceId, setHoveredServiceId] = useState<string | null>(null)
  const featuresSectionRef = useRef<HTMLElement>(null)
  const [areFeaturesVisible, setAreFeaturesVisible] = useState(false)

  // Intersection Observer for services section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsServicesVisible(entry.isIntersecting)
        })
      },
      {
        threshold: 0.2, // Trigger when 20% of the section is visible
        rootMargin: '-50px 0px', // Add some margin for better UX
      }
    )

    const currentRef = servicesSectionRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            requestAnimationFrame(() => {
              setAreFeaturesVisible(true)
            })
          } else {
            requestAnimationFrame(() => {
              setAreFeaturesVisible(false)
            })
          }
        })
      },
      {
        threshold: 0.2,
        rootMargin: '-50px 0px',
      }
    )

    const currentRef = featuresSectionRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [])
  

  useSEO({
    title: 'Binder Tech Solutions - AI-Powered Location Intelligence & Geofencing',
    description: 'Transform your business with BLIS (Binder Location Intelligence System) and IZG (Intelligent Zoning & Geofencing) platforms. AI-powered location scoring, smart zoning, and real-time asset monitoring.',
    keywords: 'location intelligence, geofencing, AI location scoring, business intelligence, asset tracking, smart zoning, location analytics, SaaS platform',
    url: 'https://bindertech.com/',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Binder Tech Solutions',
      url: 'https://bindertech.com',
      description: 'AI-Powered Location Intelligence & Geofencing Solutions',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://bindertech.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
  })

  // If user is authenticated, redirect to dashboard
  // (handled by useEffect above)

  // Show public landing page for unauthenticated users
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background-light font-display text-text-main-light antialiased">
      <div className="absolute inset-0 hero-glow pointer-events-none"></div>

      <Header />

      <main className="relative pt-32 pb-20 px-4 sm:px-10 lg:px-40 flex justify-center">
        <div className="max-w-[1200px] w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Hero Text Section */}
          <div className="flex flex-col gap-8 z-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 text-primary px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider w-fit">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              New Features Available
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] tracking-tight text-text-main-light dark:text-text-main-dark">
              Visualize Saudi Arabia <span className="text-primary">LIKE NEVER BEFORE</span>
            </h1>
            <p className="text-lg md:text-xl text-text-sub-light dark:text-text-sub-dark max-w-lg leading-relaxed">
              Empowering your business with high-precision location intelligence and spatial data analytics across Saudi Arabia.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <a
                href="mailto:contact-us@binder.com?subject=Service%20Subscription%20Request&body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20submit%20a%20service%20request.%0D%0A%0D%0AORGANIZATION%20NAME:%0D%0ASERVICE%20REQUESTED:%0D%0AADDITIONAL%20DETAILS:%0D%0A%0D%0AThank%20you."
                className="bg-primary hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-primary/30 transition-all hover:-translate-y-1"
              >
                Get Started
              </a>
            </div>
          </div>

          {/* Map Visualization */}
          <div className="relative flex justify-center items-center float-animation lg:-mr-32">
            <div className="absolute w-[140%] h-[140%] bg-primary/5 blur-[140px] rounded-full"></div>
            <div className="relative w-full aspect-[16/10] bg-gradient-to-b from-slate-100 to-slate-200 dark:from-gray-800 dark:to-gray-900 rounded-3xl border-8 border-slate-50 dark:border-gray-800 shadow-2xl p-4 flex items-center justify-center">
              <div className="w-full h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden relative border border-slate-300 dark:border-gray-700 shadow-inner">
                <img
                  className="w-full h-full object-contain"
                  alt="Light modern digital map of Saudi Arabia"
                  src="/images/KSAmap.png"
                />
                <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'linear-gradient(#2563eb 1px, transparent 1px), linear-gradient(90deg, #2563eb 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-[20%] left-[45%] pulse-pin">
                    <span className="material-symbols-outlined text-[#ef4444] text-4xl" style={{ fontVariationSettings: "'FILL' 1" }}>location_on</span>
                  </div>
                  <div className="absolute top-[45%] left-[30%] pulse-pin" style={{ animationDelay: '0.5s' }}>
                    <span className="material-symbols-outlined text-[#f59e0b] text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>location_on</span>
                  </div>
                  <div className="absolute top-[40%] left-[50%] pulse-pin" style={{ animationDelay: '1.2s' }}>
                    <span className="material-symbols-outlined text-[#22c55e] text-4xl" style={{ fontVariationSettings: "'FILL' 1" }}>location_on</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-12 -right-4 w-24 h-24 bg-white/80 dark:bg-gray-800/80 border border-white/50 dark:border-gray-700/50 backdrop-blur-md rounded-2xl flex items-center justify-center float-animation"
            style={{
              animationDelay: '2s',
              boxShadow: '0 0 12px rgba(0, 0, 0, 0.25)',
            }}>
              <span className="material-symbols-outlined text-primary text-4xl">analytics</span>
            </div>
            <div className="absolute -bottom-8 -left-4 w-20 h-20 bg-white/80 dark:bg-gray-800/80 border border-white/50 dark:border-gray-700/50 backdrop-blur-md rounded-2xl flex items-center justify-center float-animation"
            style={{
              animationDelay: '-2s',
              boxShadow: '0 0 12px rgba(0, 0, 0, 0.25)',
            }}>
              <span className="material-symbols-outlined text-primary text-3xl">map</span>
            </div>
          </div>
        </div>
      </main>

      {/* Services Section */}
      <section
        id="services"
        ref={servicesSectionRef}
        className="py-24 px-4 sm:px-10 lg:px-40 relative"
      >
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="relative z-20" style={{ marginBottom: '2rem' }}>
            <h2 className="text-4xl font-black mb-4 text-text-main-light dark:text-text-main-dark">Our Specialized Services</h2>
            <p className="text-text-sub-light dark:text-text-sub-dark text-lg max-w-xl">Advanced spatial intelligence at your fingertips. Tailored solutions for Saudi Arabia's unique geographical challenges.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 relative">
            {/* Service Cards - On Top */}
            <div className="relative flex flex-col gap-10">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  onMouseEnter={() => window.innerWidth >= 1024 && setHoveredServiceId(service.id)}
                  onMouseLeave={() => window.innerWidth >= 1024 && setHoveredServiceId(null)}
                  className="
                    group bg-white dark:bg-gray-800
                    p-8
                    rounded-xl
                    border border-slate-100 dark:border-gray-700
                    shadow-lg hover:shadow-2xl
                    transition-all
                    hover:-translate-y-2
                    relative"
                  style={{
                    marginTop: index === 0 ? 0 : '-1.5rem',
                    zIndex: services.length - index,
                  }}>
                  <h3 className="text-2xl font-bold mb-4 text-text-main-light dark:text-text-main-dark">{service.name}</h3>
                  <p className="text-text-sub-light dark:text-text-sub-dark text-lg leading-relaxed" style={{ marginBottom: '1rem' }}>
                    {service.description}
                  </p>
                  <Link
                    to={`/services/${service.id}/demo`}
                    className="inline-flex items-center text-primary font-bold group-hover:gap-3 transition-all"
                  >
                    Learn More <span className="material-symbols-outlined text-base ml-1">arrow_forward</span>
                  </Link>
                </div>
              ))}</div>
          </div>
        </div>
        <div className="absolute top-0 right-0 h-full w-[45vw] pointer-events-none">
          {/* Service Images */}
          <div className="relative h-full w-full overflow-hidden pt-32">
            {/* Default image (shown when NOT hovering) */}
            <img
              src="/images/DefaultServiceImg.png"
              alt="Default map of Saudi Arabia"
              className={`
                absolute inset-0
                w-full h-full
                object-cover
                transition-all duration-700 ease-out
                will-change-transform

                ${isServicesVisible ? 'opacity-100' : 'opacity-0'}

                lg:${hoveredServiceId === null
                  ? 'opacity-100 translate-x-[-12px]'
                  : 'opacity-0 translate-x-10'
                }
              `}
            />

            {services.map((service) => (
              <img
                key={service.id}
                src={service.image}
                alt={service.name}
                className={`
                  hidden lg:block
                  absolute inset-0
                  w-full h-full
                  object-cover
                  transition-all duration-700 ease-out
                  will-change-transform
                  ${isServicesVisible && hoveredServiceId === service.id
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 translate-x-10'
                  }
                `}
              />
            ))}
          </div>
          <div className="
            pointer-events-none
            absolute left-0 top-0 h-full w-32
            bg-gradient-to-r
            from-background-light/70
            to-transparent
            dark:from-background-dark/60
            "
            />
        </div>
      </section>

      {/* Feature Highlights */}
      <section
        id="features"
        ref={featuresSectionRef}
        className="py-24 px-4 sm:px-10 lg:px-40 bg-slate-50/50 dark:bg-gray-900/50 border-y border-slate-100 dark:border-gray-800 relative"
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4 text-text-main-light dark:text-text-main-dark">Key Features</h2>
            <p className="text-text-sub-light dark:text-text-sub-dark text-lg">Scale your location intelligence as your business grows.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`
                  transition-all duration-800 ease-out
                  will-change-transform
                  ${areFeaturesVisible
                    ? 'opacity-100 translate-y-0 scale-100'
                    : 'opacity-0 translate-y-14 scale-95'
                  }
                `}
                style={{
                  transitionDelay: `${index * 200}ms`,
                }}
              >
                <FeatureHighlight {...feature} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

